import React, { FC, useCallback, useEffect, useRef, useState, memo } from 'react';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const SOLVE_TOLERANCE_PERCENTAGE = 0.028;
const RANDOM_OFFSET_FACTOR = 0.7;
const RANDOM_OFFSET_CENTER = 0.4;
const MAX_LOAD_RETRIES = 50; // 5 seconds max (50 * 100ms)

interface Tile {
  tileOffsetX: number;
  tileOffsetY: number;
  tileWidth: number;
  tileHeight: number;
  correctPosition: number;
  currentPosXPerc: number;
  currentPosYPerc: number;
  solved: boolean;
}

interface JigsawPuzzleProps {
  imageSrc: string;
  rows?: number;
  columns?: number;
  onSolved?: () => void;
  preSolvedCount?: number;
}

type UILang = 'en' | 'es' | 'ja';

const detectSystemLang = (): UILang => {
  try {
    const tag = (navigator?.language || 'en').toLowerCase();
    if (tag.startsWith('es')) return 'es';
    if (tag.startsWith('ja')) return 'ja';
  } catch {}
  return 'en';
};

const INSTRUCTIONS: Record<UILang, string> = {
  en: 'SOLVE THE PUZZLE',
  es: 'RESUELVE EL ROMPECABEZAS',
  ja: 'パズルを解いてください',
};

// Best-effort haptics: use Vibration API when available.
const hapticImpact = (strength: 'light' | 'medium' | 'heavy' = 'light') => {
  try {
    // If the browser supports the new Haptics API in the future, prefer it.
    // @ts-expect-error experimental
    if (navigator?.haptics?.impact) {
      // @ts-expect-error experimental
      navigator.haptics.impact(strength);
      return;
    }
  } catch {}
  try {
    if (typeof navigator?.vibrate === 'function') {
      switch (strength) {
        case 'heavy':
          navigator.vibrate([10, 20, 10]);
          break;
        case 'medium':
          navigator.vibrate([10, 10]);
          break;
        default:
          navigator.vibrate(12);
      }
    }
  } catch {}
};

const JigsawPuzzle: FC<JigsawPuzzleProps> = memo(({ imageSrc, rows = 3, columns = 3, onSolved = () => {}, preSolvedCount = 6 }) => {
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [imageSize, setImageSize] = useState<{ width: number, height: number } | null>(null);
  const [rootSize, setRootSize] = useState<{ width: number, height: number } | null>(null);
  const [calculatedHeight, setCalculatedHeight] = useState<number | null>(null);
  const [resizedImageSrc, setResizedImageSrc] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [uiLang, setUiLang] = useState<UILang>(() => detectSystemLang());

  const rootElement = useRef<HTMLDivElement | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const draggingTile = useRef<{
    tile: Tile,
    elem: HTMLElement,
    pointerId: number,
    mouseOffsetX: number,
    mouseOffsetY: number,
  } | undefined>();
  const dragRAF = useRef<number | null>(null);
  const nextDragPos = useRef<{ x: number; y: number } | null>(null);
  const solvedFiredRef = useRef(false);

  // Fire onSolved exactly once when every tile is in place. Driven by state so
  // it reads the committed tiles (not stale values), and guarded by a ref so
  // StrictMode's double-invoke can't trigger it twice.
  useEffect(() => {
    if (!solvedFiredRef.current && tiles.length > 0 && tiles.every(tile => tile.solved)) {
      solvedFiredRef.current = true;
      onSolved();
    }
  }, [tiles, onSolved]);

  useEffect(() => {
    document.body.classList.add('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, []);

  useEffect(() => {
    const handler = () => setUiLang(detectSystemLang());
    window.addEventListener?.('languagechange', handler);
    return () => window.removeEventListener?.('languagechange', handler);
  }, []);

  const shuffleArray = <T,>(array: T[]): T[] => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const resizeImage = useCallback((image: HTMLImageElement, containerWidth: number, containerHeight: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const imageAspectRatio = image.width / image.height;
    const containerAspectRatio = containerWidth / containerHeight;
    let resizedImageWidth: number;
    let resizedImageHeight: number;

    if (imageAspectRatio > containerAspectRatio) {
      resizedImageWidth = containerWidth;
      resizedImageHeight = containerWidth / imageAspectRatio;
    } else {
      resizedImageWidth = containerHeight * imageAspectRatio;
      resizedImageHeight = containerHeight;
    }

    canvas.width = resizedImageWidth;
    canvas.height = resizedImageHeight;
    ctx?.drawImage(image, 0, 0, resizedImageWidth, resizedImageHeight);
    // JPEG keeps the data URL small for a photo; PNG re-encoding bloated it.
    return canvas.toDataURL('image/jpeg', 0.85);
  }, []);

  const getRandomOffset = (max: number) => Math.random() * max * RANDOM_OFFSET_FACTOR - max * RANDOM_OFFSET_CENTER;

  const calculateTilePositions = useCallback((containerSize: { width: number, height: number }, columns: number, rows: number, preSolvedCount: number): Tile[] => {
    const tileWidth = containerSize.width / columns;
    const tileHeight = containerSize.height / rows;
    const initialTiles = Array.from(Array(rows * columns).keys()).map(position => {
      const correctPosX = (position % columns) * tileWidth;
      const correctPosY = Math.floor(position / columns) * tileHeight;
      return {
        correctPosition: position,
        tileHeight,
        tileWidth,
        tileOffsetX: correctPosX,
        tileOffsetY: correctPosY,
        currentPosXPerc: 0,
        currentPosYPerc: 0,
        solved: false
      };
    });

    const shuffledTiles = shuffleArray([...initialTiles]);
    const preSolvedTiles = shuffledTiles.slice(0, preSolvedCount).map(tile => ({
      ...tile,
      currentPosXPerc: tile.tileOffsetX / containerSize.width,
      currentPosYPerc: tile.tileOffsetY / containerSize.height,
      solved: true,
    }));

    const remainingTiles = shuffledTiles.slice(preSolvedCount).map(tile => {
      const randomOffsetX = getRandomOffset(tileWidth);
      const randomOffsetY = getRandomOffset(tileHeight);
      return {
        ...tile,
        currentPosXPerc: clamp((tile.tileOffsetX + randomOffsetX) / containerSize.width, 0, 1),
        currentPosYPerc: clamp((tile.tileOffsetY + randomOffsetY) / containerSize.height, 0, 1)
      };
    });

    return [...preSolvedTiles, ...remainingTiles];
  }, []);

  const onImageLoaded = useCallback((image: HTMLImageElement, containerWidth: number, containerHeight: number) => {
    const resizedImageDataURL = resizeImage(image, containerWidth, containerHeight);
    const img = new Image();
    img.onload = () => {
      const resizedImageSize = { width: img.width, height: img.height };
      setImageSize(resizedImageSize);
      setResizedImageSrc(resizedImageDataURL);
      const scatteredTiles = calculateTilePositions({ width: containerWidth, height: containerHeight }, columns, rows, preSolvedCount);
      setTiles(scatteredTiles);
    };
    img.src = resizedImageDataURL;
  }, [rows, columns, resizeImage, calculateTilePositions, preSolvedCount]);

  const onRootElementResized = useCallback((entries: ResizeObserverEntry[]) => {
    const contentRect = entries[0].contentRect;
    const width = contentRect.width;
    const height = imageSize ? (width / imageSize.width) * imageSize.height : contentRect.height;

    setRootSize(prev => {
      // Only update when size changes to avoid unnecessary work
      if (prev && prev.width === width && prev.height === height) return prev;
      return { width, height };
    });
    setCalculatedHeight(height);

    // Update tile geometry without reshuffling on resize
    if (imageSize) {
      setTiles(prev => {
        if (!prev.length) return prev;
        const tileWidth = width / columns;
        const tileHeight = height / rows;
        return prev.map(tile => {
          const position = tile.correctPosition;
          const correctPosX = (position % columns) * tileWidth;
          const correctPosY = Math.floor(position / columns) * tileHeight;
          return {
            ...tile,
            tileWidth,
            tileHeight,
            tileOffsetX: correctPosX,
            tileOffsetY: correctPosY,
          };
        });
      });
    }
  }, [imageSize, columns, rows]);

  const onRootElementRendered = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      rootElement.current = element;
      const observer = new ResizeObserver(onRootElementResized);
      observer.observe(element);
      resizeObserver.current = observer;

      if (imageSize) {
        const width = element.offsetWidth;
        const height = (width / imageSize.width) * imageSize.height;
        setRootSize({ width, height });
        setCalculatedHeight(height);
      }
    }
  }, [onRootElementResized, imageSize]);

  useEffect(() => {
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    let retryCount = 0;
    const handleInitialLoad = () => {
      if (rootElement.current && imageSrc) {
        const { width: containerWidth, height: containerHeight } = rootElement.current.getBoundingClientRect();
        if (containerWidth === 0 || containerHeight === 0) {
          if (retryCount < MAX_LOAD_RETRIES) {
            retryCount++;
            setTimeout(handleInitialLoad, 100);
          }
          return;
        }
        const image = new Image();
        image.onload = () => onImageLoaded(image, containerWidth, containerHeight);
        image.src = imageSrc;
      }
    };

    if (isMounted) {
      handleInitialLoad();
    }
  }, [imageSrc, onImageLoaded, isMounted]);

  const onTilePointerDown = useCallback((tile: Tile, event: React.PointerEvent) => {
    if (!tile.solved && rootElement.current) {
      const target = event.currentTarget as HTMLDivElement;
      try { target.setPointerCapture(event.pointerId); } catch {}

      const targetRect = target.getBoundingClientRect();
      draggingTile.current = {
        tile,
        elem: target,
        pointerId: event.pointerId,
        mouseOffsetX: event.clientX - targetRect.x,
        mouseOffsetY: event.clientY - targetRect.y,
      };
      target.classList.add('jigsaw-puzzle__piece--dragging');
    }
  }, []);

  const onRootPointerMove = useCallback((event: React.PointerEvent) => {
    if (draggingTile.current && rootElement.current && rootSize) {
      if (event.pointerId !== draggingTile.current.pointerId) return;
      event.stopPropagation();
      const rootRect = rootElement.current.getBoundingClientRect();
      const x = clamp(
        event.clientX - rootRect.left - draggingTile.current.mouseOffsetX,
        0,
        rootSize.width - draggingTile.current.elem.offsetWidth
      );
      const y = clamp(
        event.clientY - rootRect.top - draggingTile.current.mouseOffsetY,
        0,
        rootSize.height - draggingTile.current.elem.offsetHeight
      );
      nextDragPos.current = { x, y };
      if (dragRAF.current == null) {
        dragRAF.current = requestAnimationFrame(() => {
          dragRAF.current = null;
          if (draggingTile.current && nextDragPos.current) {
            const { x, y } = nextDragPos.current;
            draggingTile.current.elem.style.setProperty('left', `${x}px`);
            draggingTile.current.elem.style.setProperty('top', `${y}px`);
          }
        });
      }
    }
  }, [rootSize]);

  const onRootPointerUp = useCallback((event: React.PointerEvent) => {
    if (draggingTile.current && rootSize) {
      if (event.pointerId !== draggingTile.current.pointerId) return;
      draggingTile.current.elem.classList.remove('jigsaw-puzzle__piece--dragging');
      const draggedTile = draggingTile.current.tile;
      const draggedToPercentage = {
        x: clamp(draggingTile.current.elem.offsetLeft / rootSize.width, 0, 1),
        y: clamp(draggingTile.current.elem.offsetTop / rootSize.height, 0, 1)
      };
      const targetPositionPercentage = {
        x: draggedTile.tileOffsetX / rootSize.width,
        y: draggedTile.tileOffsetY / rootSize.height
      };
      const isSolved =
        Math.abs(targetPositionPercentage.x - draggedToPercentage.x) <= SOLVE_TOLERANCE_PERCENTAGE &&
        Math.abs(targetPositionPercentage.y - draggedToPercentage.y) <= SOLVE_TOLERANCE_PERCENTAGE;

      setTiles(prevState =>
        prevState.map(tile =>
          tile.correctPosition === draggedTile.correctPosition
            ? {
                ...draggedTile,
                currentPosXPerc: isSolved ? draggedTile.tileOffsetX / rootSize.width : draggedToPercentage.x,
                currentPosYPerc: isSolved ? draggedTile.tileOffsetY / rootSize.height : draggedToPercentage.y,
                solved: isSolved
              }
            : tile
        )
      );
      // Completion is detected by the effect watching `tiles` — see below.
      // (Calling onSolved here would read stale state, since the updater above
      // runs asynchronously during render.)

      if (isSolved) {
        const el = draggingTile.current.elem;
        el.classList.add('jigsaw-puzzle__piece--snapping');
        el.style.setProperty('left', `${draggedTile.tileOffsetX}px`);
        el.style.setProperty('top', `${draggedTile.tileOffsetY}px`);
        // Remove snapping class after animation completes to avoid affecting future drags
        window.setTimeout(() => {
          el.classList.remove('jigsaw-puzzle__piece--snapping');
        }, 180);
        // Haptic feedback on supported devices
        hapticImpact('medium');
      }
      try { (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId); } catch {}
      draggingTile.current = undefined;
    }
  }, [rootSize]);

  if (!imageSrc) {
    return null;
  }

  const instructionString = INSTRUCTIONS[uiLang];
  const instructionsText = instructionString.split('').map((char, index) => (
    <span key={index}>{char === ' ' ? '\u00A0' : char}</span>
  ));

  return (
    <>
      <h1 className="puzzle-instructions" aria-label={instructionString}>
        {instructionsText}
      </h1>
      <div
        ref={onRootElementRendered}
        className="jigsaw-puzzle"
        style={{
          width: '100%',
          height: calculatedHeight ? `${calculatedHeight}px` : '100%',
          position: 'relative',
          touchAction: 'none',
          userSelect: 'none',
        }}
        onPointerMove={onRootPointerMove}
        onPointerUp={onRootPointerUp}
        onDragStart={event => {
          event.stopPropagation();
          event.preventDefault();
        }}
        onDragOver={event => {
          event.stopPropagation();
          event.preventDefault();
        }}
      >
        {rootSize && tiles.map(tile => (
          <div
            onPointerDown={event => onTilePointerDown(tile, event)}
            key={tile.correctPosition}
            className={`jigsaw-puzzle__piece ${tile.solved ? ' jigsaw-puzzle__piece--solved' : ' jigsaw-puzzle__piece--unsolved'}`}
            data-position={tile.correctPosition}
            style={{
              position: 'absolute',
              height: `${100 / rows}%`,
              width: `${100 / columns}%`,
              backgroundImage: `url(${resizedImageSrc})`,
              backgroundSize: `${rootSize.width}px ${rootSize.height}px`,
              backgroundPosition: `${-tile.tileOffsetX}px ${-tile.tileOffsetY}px`,
              left: `${tile.currentPosXPerc * 100}%`,
              top: `${tile.currentPosYPerc * 100}%`,
              willChange: 'left, top'
            }}
          />
        ))}
      </div>
    </>
  );
});

export default JigsawPuzzle;
